/* eslint-disable radix */
import React from "react";
import moment from "moment";
import { Row, Col } from "antd";
import getCurrencySign from "utils/currency";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import resizeOverflowingPrice from "utils/resizeOverflowingPrice";
import {
  ProgramDescription,
  ProgramInfoContainer,
  SubscriptionStatusTag,
  Price,
  CurrencySign,
  PerUnitTime,
} from "common/program_selector";
import { H4 } from "common/text";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import GET_LANGUAGE_DETAILS from "graphql/queries/GetLanguageDetails";

import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";

import {
  LittleBoxWrapper,
  LittleBoxHeader,
  LittleBoxDescription,
  TableProgramHeader,
} from "../styles";

type TMyPackageTable = {
  activeTrialStatus: string;
  currency: string | undefined;
  endDate: Date;
};

type TLittleBox = {
  header: string;
  description: string | number;
  date?: string;
  finalBox?: boolean; // if final box, then marginRight = -0.5px
  renew: boolean;
  currency?: string;
};

const LittleBox = ({
  header,
  description,
  date,
  finalBox = false,
  renew,
  currency,
}: TLittleBox) => {
  const textColor = renew ? "#1070b6" : "gray";
  return (
    <LittleBoxWrapper style={{ marginRight: finalBox ? -0 : -2 }} renew={renew}>
      <LittleBoxHeader renew={renew}>{header}</LittleBoxHeader>
      <LittleBoxDescription
        renew={renew}
        dangerouslySetInnerHTML={{
          __html: getCurrencySign(currency?.toString() || "").concat(
            description.toString()
          ),
        }}
      />
      {date && renew && (
        <p style={{ fontSize: 13, color: textColor, marginTop: -3 }}>{date}</p>
      )}
    </LittleBoxWrapper>
  );
};

const MyPackageTable = ({
  activeTrialStatus,
  currency,
  endDate,
}: TMyPackageTable) => {
  const { t } = useTranslation("my_credits");

  const { data: info } = useQuery(GET_CURRENT_USER_INFO);

  const { data: languageDetails } = useQuery(GET_LANGUAGE_DETAILS, {
    variables: {
      id: info?.me.currentLanguage.id || "1",
    },
  });

  const costCategory = languageDetails?.languageDetails.smallGroupCredit || 0;

  const renderActiveSubscription = () => {
    const ActiveTag = (
      <SubscriptionStatusTag>{t("ACTIVE")}</SubscriptionStatusTag>
    );
    const PausedTag = (
      <SubscriptionStatusTag gray>{t("CANCELED")}</SubscriptionStatusTag>
    );
    const PaidTag = (
      <SubscriptionStatusTag gray style={{ width: "auto" }}>
        {t("PAID")}
      </SubscriptionStatusTag>
    );

    const renderSubscriptionStatusTag = () => {
      if (activeTrialStatus === "trialing") {
        return ActiveTag;
      }
      if (activeTrialStatus === "canceled") {
        return PausedTag;
      }
      return PaidTag;
    };

    const textColor = activeTrialStatus === "trialing" ? "#1070b6" : "gray";

    return (
      <TableProgramHeader checked renew={activeTrialStatus === "trialing"}>
        <ProgramInfoContainer gutter={[10, 10]}>
          <ProgramDescription sm={16} xs={12} style={{ padding: "5px" }}>
            <div style={{ whiteSpace: "normal", color: textColor }}>Trial</div>
            {renderSubscriptionStatusTag()}
          </ProgramDescription>
          <Price
            sm={8}
            xs={12}
            style={{
              fontSize: resizeOverflowingPrice(outputManuallySetPrice(0)),
              color: textColor,
            }}
          >
            <CurrencySign>
              <span // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: getCurrencySign(currency?.toString() || ""),
                }}
              />
            </CurrencySign>
            {outputManuallySetPrice(0)}
            <PerUnitTime>{t("/month")}</PerUnitTime>
          </Price>
        </ProgramInfoContainer>
      </TableProgramHeader>
    );
  };

  const endTime = moment(new Date(endDate)).format("MMM DD, YYYY") || "";
  const purchaseDate =
    moment(new Date(endDate).getTime() - 7 * 24 * 60 * 60 * 1000).format(
      "MMM DD, YYYY"
    ) || "";
  const currentLanguage = info?.me.currentLanguage.englishName;

  return (
    <Row gutter={[16, 0]} style={{ marginTop: 10 }}>
      <Col xs={24} md={12}>
        <Row>
          {renderActiveSubscription()}
          <Col span={12}>
            <LittleBox
              description={purchaseDate}
              header="Purchase Date"
              renew={activeTrialStatus === "trialing"}
            />
          </Col>
          <Col span={6}>
            <LittleBox
              date={endTime}
              description={costCategory * 3}
              header="Credits"
              renew={activeTrialStatus === "trialing"}
            />
          </Col>
          <Col span={6}>
            <LittleBox
              date={endTime}
              currency={currency}
              description="0"
              header="Payment"
              finalBox
              renew={activeTrialStatus === "trialing"}
            />
          </Col>
        </Row>
      </Col>
      <Col xs={24} md={12}>
        <H4>COSTS</H4>
        <p>
          {currentLanguage} Group Class:{" "}
          <strong>{costCategory} credits/class</strong>
          <br />
          {currentLanguage} 1-on-1 Class:{" "}
          <strong>
            {Number.parseFloat(costCategory || "0") * 3} credits/class
          </strong>
          <br />
          Credits are interchangeable between group/1-on-1 classes and
          languages.
        </p>
      </Col>
    </Row>
  );
};

export default MyPackageTable;
